import AccountingExpense, { NewOrUpdateAccountingExpense } from '@/app/models/accountingExpense';
import {
  useCreateAccountingExpenseMutation,
  useGetAccountingExpenseByCodeQuery,
  useUpdateAccountingExpenseMutation,
} from '@/app/services/accountingExpenseApi';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BuildingLinksGrid from './BuildingLinks';
import AccountingExpenseCodeField from './CodeField';
import AccountingExpenseDescriptionField from './DescriptionField';

type NormalizedBuilding = {
  buildingId: number;
  normalizedSubTypes: string[];
};

interface ExpenseNewEditScreenProps {
  expense: AccountingExpense | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function ExpenseNewEditScreen(props: ExpenseNewEditScreenProps) {
  const { expense, setDrawerOpen } = props;
  const { t } = useTranslation(['expense', 'common']);
  const { data: editingExpense } = useGetAccountingExpenseByCodeQuery(expense?.expense?.code || '');
  const [code, setCode] = useState(expense ? expense.expense.code : '');
  const [description, setDescription] = useState(
    editingExpense && editingExpense?.expense ? editingExpense?.expense.description : '',
  );
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [buildings, setBuildings] = useState(expense && editingExpense ? editingExpense?.buildings : []);
  const [createAccountingExpense] = useCreateAccountingExpenseMutation();
  const [updateAccountingExpense] = useUpdateAccountingExpenseMutation();

  useEffect(() => {
    const enabled = code && description;
    setSaveDisabled(!enabled);
  }, [code, description]);

  useEffect(() => {
    if (expense && editingExpense) {
      setDescription(editingExpense.expense.description);
      setBuildings(editingExpense?.buildings);
    }
  }, [editingExpense, expense]);

  function normalizeBuildings(buildingsList: typeof buildings): NormalizedBuilding[] {
    return buildingsList
      .map(({ building, subTypes }) => ({
        buildingId: building.id,
        normalizedSubTypes: subTypes
          .map((subType) => `${subType.code.toUpperCase()}|${subType.name.toUpperCase()}`)
          .sort(),
      }))
      .sort((a, b) => a.buildingId - b.buildingId);
  }

  function areNormalizedBuildingsEqual(current: NormalizedBuilding, original: NormalizedBuilding): boolean {
    if (current.buildingId !== original.buildingId) return false;
    if (current.normalizedSubTypes.length !== original.normalizedSubTypes.length) return false;

    return current.normalizedSubTypes.every((subtype, index) => subtype === original.normalizedSubTypes[index]);
  }

  function areBuildingsEqual(currentBuildings: typeof buildings, originalBuildings: typeof buildings): boolean {
    if (currentBuildings.length !== originalBuildings.length) return false;

    const normalizedCurrent = normalizeBuildings(currentBuildings);
    const normalizedOriginal = normalizeBuildings(originalBuildings);

    return normalizedCurrent.every((current, i) => areNormalizedBuildingsEqual(current, normalizedOriginal[i]));
  }

  const handleSave = async () => {
    const newOrUpdateAccountingExpense: NewOrUpdateAccountingExpense = {
      buildings: buildings.map((building) => ({
        buildingId: building.building.id,
        subTypes: building.subTypes,
      })),
      expense: {
        code,
        description: description || '',
      },
    };
    const execute = expense ? updateAccountingExpense : createAccountingExpense;
    const response = await execute(newOrUpdateAccountingExpense);
    if ('data' in response) {
      toast.success(
        t('Conta contábil salva com sucesso!', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
      setDrawerOpen(false);
    }
  };

  return (
    <>
      <Typography variant="h1">{!expense ? t('new.expense') : t('update.expense')}</Typography>
      <div style={{ marginTop: '24px' }}>
        <Grid container spacing={3}>
          <Grid item xs={5}>
            <AccountingExpenseCodeField
              label={t('common:code')}
              value={code}
              onChange={(newValue: string) => setCode(newValue)}
            />
          </Grid>
          <Grid item xs={7}>
            <AccountingExpenseDescriptionField
              label={t('expense.name')}
              value={description}
              onChange={(newValue: string) => setDescription(newValue)}
            />
          </Grid>
        </Grid>
      </div>
      <Divider sx={{ marginY: 2 }} />
      <Box display="flex" flexDirection="column" justifyContent="flex-end">
        <Typography variant="h2">{t('associated.buildings')}</Typography>
        <Typography sx={{ color: '#888888' }}>{t('select.buildings.associated.expense')}</Typography>
      </Box>
      <BuildingLinksGrid
        selectedBuilding={buildings || []}
        onChangeBuildings={(newValue) => {
          setBuildings(newValue);
        }}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          marginBottom: '8px',
          marginTop: 'auto',
        }}
      >
        <Button
          style={{
            marginRight: '16px',
          }}
          variant="secondary"
          onClick={() => setDrawerOpen(false)}
        >
          {t('common:cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={() => handleSave()}
          disabled={buildings.length === 0 || areBuildingsEqual(buildings, editingExpense?.buildings || [])}
        >
          {!expense ? t('common:add') : t('common:save')}
        </Button>
      </div>
    </>
  );
}
